import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  timeout: 120000,
  headers: { "Content-Type": "application/json" },
});

// 모든 요청에 JWT 토큰 자동 첨부
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 응답 시 토큰 제거 (단, retry 플래그가 있으면 서버 일시 오류이므로 유지)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !err.response?.data?.retry) {
      localStorage.removeItem("authToken");
    }
    return Promise.reject(err);
  }
);

export async function askQuestion(question, category, chatSessionId) {
  const payload = { question, category };
  if (chatSessionId) payload.chat_session_id = chatSessionId;
  const res = await api.post("/api/ask", payload);
  return res.data;
}

export async function submitLead(leadData) {
  const res = await api.post("/api/leads", leadData);
  return res.data;
}

export async function fetchMyHistory(page = 1, perPage = 20) {
  const res = await api.get("/api/my/history", {
    params: { page, per_page: perPage },
  });
  return res.data;
}

export async function fetchChatSessions() {
  const res = await api.get("/api/my/sessions");
  return res.data.sessions;
}

export async function fetchSessionMessages(sessionId) {
  const res = await api.get(`/api/my/sessions/${sessionId}/messages`);
  return res.data.messages;
}

export async function deleteChatSession(sessionId) {
  const res = await api.delete(`/api/my/sessions/${sessionId}`);
  return res.data;
}

export async function searchLaws(query, category = "LAW", maxResults = 5) {
  const res = await api.get("/api/law/search", {
    params: { query, category, max_results: maxResults },
  });
  return res.data;
}

export async function fetchLawArticles(mst) {
  const res = await api.get(`/api/law/${mst}/articles`);
  return res.data;
}

export async function searchPrecedents(query, maxResults = 3) {
  const res = await api.get("/api/law/precedents", {
    params: { query, max_results: maxResults },
  });
  return res.data;
}

export async function fetchPrecedentDetail(precId) {
  const res = await api.get(`/api/law/precedents/${precId}`);
  return res.data;
}

// ═══════════════════════════════
// 신기능: 문서 분석
// ═══════════════════════════════

export async function analyzeDocument(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await api.post("/api/document/analyze", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return res.data;
}

export async function fetchDocumentHistory(page = 1, perPage = 20) {
  const res = await api.get("/api/document/history", {
    params: { page, per_page: perPage },
  });
  return res.data;
}

// ═══════════════════════════════
// 신기능: 사건 결과 예측
// ═══════════════════════════════

export async function predictCase(caseDescription, category = "LAW") {
  const res = await api.post("/api/predict", {
    case_description: caseDescription,
    category,
  });
  return res.data;
}

// ═══════════════════════════════
// 신기능: 서류 초안 생성
// ═══════════════════════════════

export async function generateDraft(draftType, inputData) {
  const res = await api.post("/api/draft/generate", {
    draft_type: draftType,
    input_data: inputData,
  });
  return res.data;
}

export async function fetchDraftTypes() {
  const res = await api.get("/api/draft/types");
  return res.data.types;
}

// ═══════════════════════════════
// 관리자 ERP
// ═══════════════════════════════

export async function fetchAdminDashboard() {
  const res = await api.get("/api/admin/dashboard");
  return res.data;
}

export async function fetchAdminUsers(page = 1, perPage = 50, includeDeleted = true) {
  const res = await api.get("/api/admin/users", {
    params: { page, per_page: perPage, include_deleted: includeDeleted },
  });
  return res.data;
}

export async function fetchAdminUserDetail(userId) {
  const res = await api.get(`/api/admin/users/${userId}`);
  return res.data;
}

export async function fetchAdminQueryLogs(page = 1, perPage = 50, userId = null) {
  const params = { page, per_page: perPage };
  if (userId) params.user_id = userId;
  const res = await api.get("/api/admin/query-logs", { params });
  return res.data;
}

export async function fetchAdminLeads(page = 1, perPage = 50) {
  const res = await api.get("/api/admin/leads", { params: { page, per_page: perPage } });
  return res.data;
}

export async function fetchAdminFeatureUsage(feature = "all", perPage = 50) {
  const res = await api.get("/api/admin/feature-usage", {
    params: { feature, per_page: perPage },
  });
  return res.data;
}

export default api;
